import { useState, useEffect, useContext } from "react";
import { FiTrash2, FiPlus } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import TopicService from "../../../services/TopicService";
import AuthContext from "../../../auth/AuthContext";
import ConfirmDialogBox from "../../confirmdiolog/ConfirmDiologBox";
import AlertService from "../../../services/AlertService";
import { useBreadcrumb } from "../../../context/BreadcrumbContext";

const inputClass = `
  w-full h-12 px-3 rounded border
  bg-white dark:bg-slate-900
  border-slate-300 dark:border-slate-700
  text-slate-800 dark:text-slate-200
  placeholder:text-slate-400 dark:placeholder:text-slate-500
  focus:outline-none focus:ring-1
  focus:ring-blue-400 dark:focus:ring-blue-600
`;

const labelClass = "text-base text-slate-700 dark:text-slate-300 mb-1 block";

const CreateSubTopic = () => {
  const { topicId, subTopicId  } = useParams();
  const isEditMode = Boolean(subTopicId);
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const [topics, setTopics] = useState([]);
  const [selectedTopicId, setSelectedTopicId] = useState(topicId || "");
  const [subTopicName, setSubTopicName] = useState("");
  const [subTopicDescription, setSubTopicDescription] = useState("");
  const [topicError, setTopicError] = useState("");
  const [subTopicError, setSubTopicError] = useState("");
  const [videoErrors, setVideoErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showDeleteVideoDialog, setShowDeleteVideoDialog] = useState(false);
  const [selectedVideoId, setSelectedVideoId] = useState(null);
  const [videos, setVideos] = useState([
    {
      id: Date.now().toString(),
      isNew: true,
      videoName: "",
      thumbnail: "",
      directPlay: "",
      details: "",
      quiz: false,
      quizQuestions: "",
      quizTime: "",
    },
  ]);

  const { setDynamicLabels } = useBreadcrumb();

useEffect(() => {
  const topic = topics.find((t) => t.id === topicId);

  setDynamicLabels({
    topicId,
    topicTitle: topic?.name || "",
    subTopicId,
    subTopicTitle: subTopicName || "",
  });
}, [topics, topicId, subTopicId, subTopicName]);

  useEffect(() => {
    fetchTopics();
    if (isEditMode) {
      fetchSubTopicData();
    }
  }, [subTopicId]);

  const fetchTopics = async () => {
    try {
      const response = await TopicService.GetAllTopics(authContext);
      if (response?.isSuccess && response?.data) {
        setTopics(response.data.filter((t) => !t.isDeleted));
      }
    } catch (error) {
      console.error("Error fetching topics:", error);
      AlertService.error("Failed to load topics");
    }
  };

  const fetchSubTopicData = async () => {
    try {
      setLoading(true);
      const response = await TopicService.GetSubTopicById(subTopicId, authContext);

      if (response?.isSuccess && response?.data) {
        const subTopic = response.data;
        setSelectedTopicId(subTopic.topicId || "");
        setSubTopicName(subTopic.name || "");
        setSubTopicDescription(subTopic.description || "");

        if (subTopic.videos && subTopic.videos.length > 0) {
          setVideos(
            subTopic.videos.map((v) => ({
              id: v.id,
              isNew: false,
              videoName: v.name || "",
              thumbnail: v.thumbnailUrl || "",
              directPlay: v.videoUrl || "",
              details: v.details || "",
              quiz: v.hasQuiz || false,
              quizQuestions: v.noOfQuestions || "",
              quizTime: v.duration || "",
            })),
          );
        }
      } else {
        AlertService.error(
          response?.errorMessage || "Failed to load sub topic",
        );
      }
    } catch (error) {
      console.error("Error fetching sub topic:", error);
      AlertService.error(
        error?.response?.data?.errorMessage || "Failed to load sub topic",
      );
    } finally {
      setLoading(false);
    }
  };

  const updateVideo = (id, field, value) => {
    setVideos(
      videos.map((video) =>
        video.id === id ? { ...video, [field]: value } : video,
      ),
    );

    setVideoErrors((prev) => {
      if (!prev[id]) return prev;
      const updated = { ...prev[id] };
      delete updated[field];

      return {
        ...prev,
        [id]: updated,
      };
    });
  };

  const addVideo = () => {
    setVideos([
      ...videos,
      {
        id: Date.now().toString(),
        isNew: true,
        videoName: "",
        thumbnail: "",
        directPlay: "",
        details: "",
        quiz: false,
        quizQuestions: "",
        quizTime: "",
      },
    ]);
  };

  const isExistingVideo = (video) => !video.isNew;

  const deleteVideoDirectly = (videoId) => {
    setVideos((prev) => prev.filter((v) => v.id !== videoId));
    setVideoErrors((prev) => {
      const copy = { ...prev };
      delete copy[videoId];
      return copy;
    });
  };

  const confirmDeleteVideo = async () => {
    try {
      setLoading(true);

      const response = await TopicService.DeleteVideo(
        selectedVideoId,
        authContext,
      );

      if (response?.isSuccess) {
        setVideos((prev) => prev.filter((v) => v.id !== selectedVideoId));
        AlertService.success("Video deleted successfully");
      } else {
        AlertService.error(response?.errorMessage || "Failed to delete video");
      }
    } catch (error) {
      console.error("Error deleting video:", error);
      AlertService.error(
        error?.response?.data?.errorMessage || "Failed to delete video",
      );
    } finally {
      setLoading(false);
      setSelectedVideoId(null);
    }
  };

  const handleDeleteVideo = (video) => {
    if (isExistingVideo(video)) {
      setSelectedVideoId(video.id);
      setShowDeleteVideoDialog(true);
    } else {
      deleteVideoDirectly(video.id);
    }
  };

  const handleSave = async () => {
    if (loading) return;

    let hasError = false;

    // validation
    if (!selectedTopicId) {
      setTopicError("Topic is required");
      hasError = true;
    } else {
      setTopicError("");
    }

    if (!subTopicName.trim()) {
      setSubTopicError("Sub topic name is required");
      hasError = true;
    } else {
      setSubTopicError("");
    }

    const errors = {};

    videos.forEach((v) => {
      const err = {};

      if (!v.videoName?.trim()) {
        err.videoName = "Video name is required";
      }

      if (!v.thumbnail?.trim()) {
        err.thumbnail = "Thumbnail URL is required";
      }

      if (!v.directPlay?.trim()) {
        err.directPlay = "Video URL is required";
      }

      if (Object.keys(err).length > 0) {
        errors[v.id] = err;
        hasError = true;
      }
    });

    setVideoErrors(errors);
    if (hasError) return;
    try {
      setLoading(true);

      const payload = {
        id: isEditMode ? subTopicId : undefined,
        topicId: selectedTopicId,
        name: subTopicName,
        description: subTopicDescription,
        isDeleted: false,
        videos: videos.map((v) => ({
          id: isEditMode && !v.isNew ? v.id : undefined,
          subTopicId: isEditMode ? subTopicId : undefined,
          name: v.videoName,
          details: v.details,
          videoUrl: v.directPlay,
          thumbnailUrl: v.thumbnail,
          hasQuiz: v.quiz,
          noOfQuestions: v.quiz ? Number(v.quizQuestions) || 0 : 0,
          duration: v.quiz ? Number(v.quizTime) || 0 : 0,
        })),
      };

      const response = await TopicService.SaveSubTopic(payload, authContext);

      if (response?.isSuccess) {
        AlertService.success(
          isEditMode
            ? "Sub topic updated successfully"
            : "Sub topic created successfully",
        );
        navigate("/knowledge-management/topics");
      } else {
        AlertService.error(
          response?.errorMessage || "Failed to save sub topic",
        );
      }
    } catch (error) {
      AlertService.error("Failed to save sub topic");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="">
      <div className="border rounded-md p-4 sm:p-6 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700">
        {/* TOP ROW */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className={labelClass}>
              Topic <span className="text-red-500">*</span>
            </label>

            <select
              className={`
                ${inputClass}
                ${topicError ? "border-red-500 focus:ring-red-400" : ""}
              `}
              value={selectedTopicId}
              onChange={(e) => {
                setSelectedTopicId(e.target.value);
                if (e.target.value) setTopicError("");
              }}
              onBlur={() => {
                if (!selectedTopicId) setTopicError("Topic is required");
              }}
              disabled={isEditMode || !!topicId}
            >
              <option value="">Select Topic</option>
              {topics.map((topic) => (
                <option key={topic.id} value={topic.id}>
                  {topic.name}
                </option>
              ))}
            </select>

            {topicError && (
              <p className="mt-1 text-sm text-red-500">{topicError}</p>
            )}
          </div>

          <div>
            <label className={labelClass}>Sub Topic</label>
            <input
              className={`${inputClass} ${
                subTopicError ? "border-red-500 focus:ring-red-400" : ""
              }`}
              value={subTopicName}
              onChange={(e) => {
                setSubTopicName(e.target.value);
                if (e.target.value) setSubTopicError("");
              }}
            />

            {subTopicError && (
              <p className="mt-1 text-sm text-red-500">{subTopicError}</p>
            )}
          </div>
        </div>

        <div className="mb-6">
          <label className={labelClass}>Description</label>
          <textarea
            className="
              w-full px-3 py-2 rounded border
              resize-y min-h-[6rem]
              bg-white dark:bg-slate-900
              border-slate-300 dark:border-slate-700
              text-slate-800 dark:text-slate-200
              focus:outline-none focus:ring-1
              focus:ring-blue-400 dark:focus:ring-blue-600
            "
            placeholder="Enter sub topic description"
            value={subTopicDescription}
            onChange={(e) => setSubTopicDescription(e.target.value)}
          />
        </div>

        {videos.length === 0 && (
          <div className="flex justify-end mb-6">
            <button
              onClick={addVideo}
              disabled={loading}
              className="
        bg-[#0D3744] hover:bg-[#0b2f39]
        text-white px-4 py-2 rounded
        flex items-center gap-1 transition
        disabled:opacity-50 disabled:cursor-not-allowed
      "
            >
              <FiPlus size={18} /> Add Video
            </button>
          </div>
        )}

        {videos.map((video, index) => (
          <div
            key={video.id}
            className="border rounded-md p-4 mb-5
              bg-slate-50 dark:bg-slate-800
              border-slate-200 dark:border-slate-700"
          >
            {/* ROW 1 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className={labelClass}>Video Name</label>
                <input
                  className={`${inputClass} ${
                    videoErrors[video.id]?.videoName
                      ? "border-red-500 focus:ring-red-400"
                      : ""
                  }`}
                  value={video.videoName}
                  onChange={(e) =>
                    updateVideo(video.id, "videoName", e.target.value)
                  }
                />

                {videoErrors[video.id]?.videoName && (
                  <p className="mt-1 text-sm text-red-500">
                    {videoErrors[video.id].videoName}
                  </p>
                )}
              </div>

              <div>
                <label className={labelClass}>Thumbnail URL</label>
                <input
                  className={`${inputClass} ${
                    videoErrors[video.id]?.thumbnail
                      ? "border-red-500 focus:ring-red-400"
                      : ""
                  }`}
                  value={video.thumbnail}
                  onChange={(e) =>
                    updateVideo(video.id, "thumbnail", e.target.value)
                  }
                  placeholder="Enter thumbnail URL"
                />

                {videoErrors[video.id]?.thumbnail && (
                  <p className="mt-1 text-sm text-red-500">
                    {videoErrors[video.id].thumbnail}
                  </p>
                )}
              </div>
            </div>

            {/* ROW 2 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className={labelClass}>Direct Play URL</label>
                <input
                  className={`${inputClass} ${
                    videoErrors[video.id]?.directPlay
                      ? "border-red-500 focus:ring-red-400"
                      : ""
                  }`}
                  value={video.directPlay}
                  onChange={(e) =>
                    updateVideo(video.id, "directPlay", e.target.value)
                  }
                />

                {videoErrors[video.id]?.directPlay && (
                  <p className="mt-1 text-sm text-red-500">
                    {videoErrors[video.id].directPlay}
                  </p>
                )}
              </div>

              <div>
                <label className={labelClass}>Details</label>
                <textarea
                  className="
                    w-full h-12 px-3 py-2 rounded border
                    resize-y min-h-[3rem]
                    bg-white dark:bg-slate-900
                    border-slate-300 dark:border-slate-700
                    text-slate-800 dark:text-slate-200
                    focus:outline-none focus:ring-1
                    focus:ring-blue-400 dark:focus:ring-blue-600
                  "
                  placeholder="Enter Details"
                  value={video.details}
                  onChange={(e) =>
                    updateVideo(video.id, "details", e.target.value)
                  }
                />
              </div>
            </div>

            {/* QUIZ */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end mb-4">
              <label className="flex items-center gap-2 h-12 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-5 h-5 text-slate-700 dark:text-slate-300"
                  checked={video.quiz}
                  onChange={(e) => {
                    const checked = e.target.checked;

                    setVideos((prev) =>
                      prev.map((v) =>
                        v.id === video.id
                          ? {
                              ...v,
                              quiz: checked,
                              quizQuestions: checked ? v.quizQuestions : "",
                              quizTime: checked ? v.quizTime : "",
                            }
                          : v,
                      ),
                    );
                  }}
                />

                <span className="text-base text-slate-700 dark:text-slate-300">
                  Quiz
                </span>
              </label>

              <div>
                <label className={labelClass}>Number of Questions</label>
                <input
                  type="number"
                  min={1}
                  className={`${inputClass} disabled:opacity-50 disabled:cursor-not-allowed`}
                  disabled={!video.quiz}
                  value={video.quizQuestions}
                  onChange={(e) =>
                    updateVideo(video.id, "quizQuestions", e.target.value)
                  }
                  placeholder="0"
                />
              </div>

              <div>
                <label className={labelClass}>Duration</label>
                <input
                  type="number"
                  min={1}
                  className={`${inputClass} disabled:opacity-50 disabled:cursor-not-allowed`}
                  disabled={!video.quiz}
                  value={video.quizTime}
                  onChange={(e) =>
                    updateVideo(video.id, "quizTime", e.target.value)
                  }
                  placeholder="0"
                />
              </div>
            </div>

            <div className="flex justify-between items-center mt-4">
              <button
                onClick={() => handleDeleteVideo(video)}
                disabled={loading}
                className="
                  flex items-center gap-1 text-base
                  text-red-600 dark:text-red-400
                  border border-red-300 dark:border-red-500
                  px-3 py-2 rounded
                  hover:bg-red-50 dark:hover:bg-red-950
                  transition
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
              >
                <FiTrash2 /> Delete Video
              </button>

              {video.id === videos[videos.length - 1].id && (
                <button
                  onClick={addVideo}
                  disabled={loading}
                  className="
                    bg-[#0D3744] hover:bg-[#0b2f39]
                    text-white px-4 py-2 rounded
                    flex items-center gap-1 transition
                    disabled:opacity-50 disabled:cursor-not-allowed
                  "
                >
                  <FiPlus size={18} /> Add Videos
                </button>
              )}
            </div>
          </div>
        ))}

        {/* FOOTER */}
        <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6">
          <button
            className="
              border px-6 py-2 rounded
              text-slate-700 dark:text-slate-200
              border-slate-300 dark:border-slate-600
              hover:bg-slate-100 dark:hover:bg-slate-800
              disabled:opacity-50 disabled:cursor-not-allowed
            "
            onClick={() => navigate(-1)}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="
              bg-[#0D3744] hover:bg-[#0b2f39]
              text-white sm:px-4 xl:px-8 py-2 rounded
              disabled:opacity-50 disabled:cursor-not-allowed
            "
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? "Saving..." : isEditMode ? "Update" : "Save"}
          </button>
        </div>
      </div>

      {/* DELETE VIDEO DIALOG - Only for existing videos */}
      <ConfirmDialogBox
        visible={showDeleteVideoDialog}
        onHide={() => {
          setShowDeleteVideoDialog(false);
          setSelectedVideoId(null);
        }}
        onConfirm={() => {
          setShowDeleteVideoDialog(false);
          confirmDeleteVideo();
        }}
        title={
          <>
            Are you sure you want to delete this <strong>video</strong>? <br />
            <small className="text-muted">This action cannot be undone.</small>
          </>
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </div>
  );
};

export default CreateSubTopic;
